/**
 * @file src/components/VidButtonBank/SupportBankButton.tsx
 * @exports SupportBankButton
 * @summary Restyled support button for the vID button bank, reusing existing SupportButton internals (SupportContents, FullScreenModal, SlideIn).
 */

import { CaretDown, colors, SlideIn, HelpCircle, useMeasureWatchResize } from "@cerebruminc/cerebellum";
import { useAtom, useAtomValue } from "jotai";
import { FC, useCallback } from "react";

import { useWindowDimensions } from "src/hooks";
import {
	currentModalStateAtom,
	highContrastThemeAtom,
	openSupportModalAtom,
	SupportModalState,
} from "src/shame/shame-atoms";

import FullScreenModal from "./FullScreenModal/FullScreenModal";
import SupportContents from "./SupportContents";
import { ICON_SIZE, ICON_SIZE_MOBILE } from "./VidButtonBank.styles";
import { IconBox } from "src/styles/SharedStyles";

interface SupportBankButtonProps {
	ButtonSlot: FC<{
		"aria-label"?: string;
		children: React.ReactNode;
		onClick: () => void;
		ref?: React.Ref<HTMLButtonElement>;
		type?: React.ButtonHTMLAttributes<HTMLButtonElement>["type"];
		$highContrast?: boolean;
	}>;
}

/** Support button that opens a slide-in panel (desktop) or full-screen modal (mobile) with a help request form. */
export const SupportBankButton: FC<SupportBankButtonProps> = ({ ButtonSlot }) => {
	const [show, setShow] = useAtom(openSupportModalAtom);
	const [targetRef, targetMeasure] = useMeasureWatchResize();
	const [currentState, setCurrentState] = useAtom(currentModalStateAtom);
	const isHighContrast = useAtomValue(highContrastThemeAtom);

	const measureOffset = { ...targetMeasure };
	measureOffset.right -= 18; // give some breathing room on the right edge of the screen

	const { width, height } = useWindowDimensions();
	const mobileMode = width < 450;

	const handleClick = useCallback(() => {
		setShow((previous) => {
			if (previous && currentState !== SupportModalState.FORM) {
				setTimeout(() => setCurrentState(SupportModalState.FORM), 400);
			}
			return !previous;
		});
	}, [setShow, currentState, setCurrentState]);

	return (
		<>
			<ButtonSlot
				aria-label="Get help"
				$highContrast={isHighContrast}
				onClick={handleClick}
				ref={targetRef}
				type="button"
			>
				<IconBox $size={mobileMode ? ICON_SIZE_MOBILE : ICON_SIZE}>
					{show ? (
						<CaretDown fill={isHighContrast ? colors.BLACK : colors.BLUE_120} />
					) : (
						<HelpCircle fill={isHighContrast ? colors.BLACK : colors.BLUE_120} />
					)}
				</IconBox>
			</ButtonSlot>
			{mobileMode && (
				<FullScreenModal show={show}>
					<SupportContents onClose={() => setShow(false)} fullScreen={true} width={width} height={height} />
				</FullScreenModal>
			)}
			{!mobileMode && (
				<SlideIn
					contentWidth={390}
					clickOut={() => setShow(false)}
					height={height}
					openUp
					show={show}
					targetMeasure={measureOffset}
				>
					<SupportContents width={width} />
				</SlideIn>
			)}
		</>
	);
};
